import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";
import {
  Bell,
  X,
  Check,
  CheckCheck,
  Trash2,
  Loader2,
  FileText,
  DollarSign,
  Star,
  Building2,
  CheckCircle2,
  UserCheck,
  MessageSquare,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { timeAgo } from "@/utils/dateFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Notification type icons and colors
const notificationConfig = {
  welcome: { icon: UserCheck, color: "text-green-500", bg: "bg-green-50" },
  new_project: { icon: FileText, color: "text-blue-500", bg: "bg-blue-50" },
  estimate_received: { icon: DollarSign, color: "text-amber-500", bg: "bg-amber-50" },
  estimate_accepted: { icon: CheckCircle2, color: "text-green-500", bg: "bg-green-50" },
  estimate_rejected: { icon: X, color: "text-red-500", bg: "bg-red-50" },
  business_approved: { icon: Building2, color: "text-green-500", bg: "bg-green-50" },
  business_rejected: { icon: Building2, color: "text-red-500", bg: "bg-red-50" },
  project_completed: { icon: CheckCircle2, color: "text-blue-500", bg: "bg-blue-50" },
  new_review: { icon: Star, color: "text-amber-500", bg: "bg-amber-50" },
  default: { icon: Bell, color: "text-slate-500", bg: "bg-slate-50" },
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/notifications?limit=20`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      if (showLoading) {
        setError("Failed to load notifications");
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial fetch and polling setup
  useEffect(() => {
    fetchNotifications(true);
    
    // Poll every 45 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchNotifications(false);
    }, 45000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark single notification as read
  const markAsRead = async (notificationId, link) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Navigate if link provided
      if (link) {
        setIsOpen(false);
        navigate(link);
      }
    } catch (err) {
      toast.error(t("notifications.failedMarkRead"));
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success(t("notifications.allMarkedRead"));
    } catch (err) {
      toast.error(t("notifications.failedMarkAll"));
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/${notificationId}`);
      const notif = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notif && !notif.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      toast.error(t("notifications.failedDelete"));
    }
  };

  // Get notification icon config
  const getNotifConfig = (type) => {
    return notificationConfig[type] || notificationConfig.default;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-slate-300 hover:text-white" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown - opens upward from sidebar position */}
      {isOpen && (
        <div
          className="absolute bottom-full left-0 mb-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-[#0B1F3B] text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              <span className="font-semibold">{t("notifications.title")}</span>
              {unreadCount > 0 && (
                <Badge className="bg-[#D4AF37] text-[#0B1F3B] text-xs">
                  {unreadCount} {t("notifications.new")}
                </Badge>
              )}
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-white/10 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Actions Bar */}
          {notifications.length > 0 && (
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <button
                onClick={markAllAsRead}
                disabled={unreadCount === 0}
                className="text-xs text-[#0B1F3B] hover:text-[#D4AF37] disabled:text-slate-400 disabled:cursor-not-allowed flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3 h-3" />
                Mark all read
              </button>
              <span className="text-xs text-slate-500">
                {notifications.length} {notifications.length !== 1 ? t("notifications.notifications") : t("notifications.notification")}
              </span>
            </div>
          )}

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
              </div>
            ) : error ? (
              <div className="text-center py-12 px-4">
                <p className="text-red-500 text-sm">{error}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fetchNotifications(true)}
                  className="mt-2 text-[#0B1F3B]"
                >
                  {t("notifications.tryAgain")}
                </Button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12 px-4" data-testid="notification-empty">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                  <Bell className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-600 font-medium">{t("notifications.noNotifications")}</p>
                <p className="text-slate-400 text-sm mt-1">
                  {t("notifications.notifyWhen")}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((notif) => {
                  const config = getNotifConfig(notif.type);
                  const IconComponent = config.icon;
                  
                  return (
                    <div
                      key={notif.id}
                      onClick={() => !notif.is_read && markAsRead(notif.id, notif.link)}
                      className={`px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors ${
                        !notif.is_read ? "bg-blue-50/50" : ""
                      }`}
                      data-testid={`notification-item-${notif.id}`}
                    >
                      <div className="flex gap-3">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                          <IconComponent className={`w-5 h-5 ${config.color}`} />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm font-medium ${!notif.is_read ? "text-[#0B1F3B]" : "text-slate-600"}`}>
                              {notif.title}
                            </p>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {!notif.is_read && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full" />
                              )}
                              <button
                                onClick={(e) => deleteNotification(notif.id, e)}
                                className="p-1 hover:bg-slate-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`delete-notification-${notif.id}`}
                              >
                                <Trash2 className="w-3 h-3 text-slate-400 hover:text-red-500" />
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                            {notif.message}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            {timeAgo(notif.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
